/**
 * @file The class to host frontend application-wide constants
 * @author Simple Formations
 */

export class Constants {
  static get HELLO_SIGN() {
    return {
      CLIENT_ID: process.env.REACT_APP_HELLO_SIGN_CLIENT_ID,
      API_KEY: process.env.REACT_APP_HELLO_SIGN_API_KEY,
    };
  }

  static get COMPANY_OFFICIAL_ID_TYPES() {
    return [
      { value: "Kenyan Citizen", label: "Kenyan Citizen" },
      { value: "Foreign Resident", label: "Foreign Resident" },
      { value: "Foreigner", label: "Foreigner" },
      { value: "Local Company", label: "Local Company" },
      { value: "Foreign Company", label: "Foreign Company" },
      { value: "Other e.g Parastatals", label: "Other e.g Parastatals" },
      { value: "Minor", label: " Minor" },
    ];
  }

  static get OWNERS_ID_TYPES() {
    return [
      { value: "Kenyan Citizen", label: "Kenyan Citizen" },
      { value: "Foreign Resident", label: "Foreign Resident" },
      { value: "Foreigner", label: "Foreigner" },
      { value: "Local Company", label: "Local Company" },
      { value: "Foreign Company", label: "Foreign Company" },
      { value: "Other e.g Parastatals", label: "Other e.g Parastatals" },
    ];
  }

  static get MAX_JOINT_SHAREHOLDER_LIMIT() {
    return 4;
  }

  // public limited company
  static get MAX_PLC_COMPANY_SECRETARIES_LIMIT() {
    return 2;
  }

  // private limited company
  static get MAX_PVT_COMPANY_SHAREHOLDERS_LIMIT() {
    return 50;
  }
  static get MAX_PVT_COMPANY_SECRETARIES_LIMIT() {
    return 2;
  }

  static get COMPANIES_LIMIT_FOR_UNVERIFIED_USER() {
    return 1;
  }

  // Limited Liability Partnership organizations
  static get MIN_LLP_ORGANIZATION_PARTNERS_LIMIT() {
    return 2;
  }

  static get MIN_LLP_ORGANIZATION_MANAGERS_LIMIT() {
    return 1;
  }

  // Limited Partnership organizations
  static get MIN_LP_ORGANIZATION_GENERAL_PARTNER_LIMIT() {
    return 1;
  }
  static get MIN_LP_ORGANIZATION_LIMITED_PARTNER_LIMIT() {
    return 1;
  }
  static get MAX_LP_ORGANIZATION_PARTNERS_LIMIT() {
    return 20;
  }

  // General Partnership organizations
  static get MIN_GP_ORGANIZATION_PARTNER_LIMIT() {
    return 2;
  }
  static get MAX_GP_ORGANIZATION_PARTNERS_LIMIT() {
    return 20;
  }

  // Sole Proprietorship organizations
  static get MIN_SP_ORGANIZATION_PARTNER_LIMIT() {
    return 1;
  }
  static get MAX_SP_ORGANIZATION_PARTNER_LIMIT() {
    return 1;
  }

  static get MIN_NON_REDEEMABLE_CLASS() {
    return 1;
  }

  static get MIN_VOTING_SHARES_CLASS() {
    return 1;
  }

  static get INDIVIDUAL_BO_OPTIONS() {
    return [
      { value: "Kenyan Citizen", label: "Kenyan Citizen" },
      { value: "Foreign Resident", label: "Foreign Resident" },
      { value: "Foreigner", label: "Foreigner" },
      { value: "Minor", label: "Minor" },
      { value: "Trust", label: "Trust" },
    ];
  }

  static get CORPORATE_BO_OPTIONS() {
    return [
      { value: "Public Limited", label: "Public Limited" },
      { value: "Private Limited", label: "Private Limited" },
      {
        value: "Limited Liability Partnership",
        label: "Limited Liability Partnership",
      },
      { value: "Limited Partnership", label: "Limited Partnership" },
      { value: "Trust", label: "Trust" },
      {
        value: "Company Limited By Guarantee/NGO",
        label: "Company Limited By Guarantee/NGO",
      },
      { value: "State Owned Enterprise", label: "State Owned Enterprise" },
    ];
  }

  static get CS_AND_AP_ENTITY_TYPES() {
    return [
      {
        value: "Limited Liability Partnership",
        label: "Limited Liability Partnership",
      },
      { value: "Limited Partnership", label: "Limited Partnership" },
      { value: "General Partnership", label: "General Partnership" },
      { value: "Sole Proprietorship", label: "Sole Proprietorship" },
    ];
  }

  static get OTHER_PARASTATALS_ENTITY_TYPES() {
    return [
      { value: "Trust", label: "Trust" },
      {
        value: "Company Limited By Guarantee/NGO",
        label: "Company Limited By Guarantee/NGO",
      },
      { value: "State Owned Enterprise", label: "State Owned Enterprise" },
    ];
  }

  static get PARTNERSHIP_OPTIONS() {
    return [
      {
        value: "Limited Liability Partnership",
        label: "Limited Liability Partnership",
      },
      { value: "Limited Partnership", label: "Limited Partnership" },
      { value: "General Partnership", label: "General Partnership" },
      { value: "Sole Proprietorship", label: "Sole Proprietorship" },
    ];
  }

  static get ON_BOARDING_FORM_FIELDS() {
    return {
      "Kenyan Citizen": {
        identificationDetails: [
          {
            key: "nationalIDNumber",
            label: "National ID",
            showFilePreview: true,
            fileName: "nationalID",
          },
        ],
      },
      "Foreign Resident": {
        identificationDetails: [
          {
            key: "foreignCertificateID",
            label: "Foreign Certificate ID",
            showFilePreview: true,
            fileName: "foreignerCert",
          },
          {
            key: "foreignCertificateDateOfExpiry",
            label: "Foreign Certificate Date Of Expiry",
            isDate: true,
            fileName: "foreignCertificateDateOfExpiry",
          },
        ],
      },
      Foreigner: {
        identificationDetails: [
          { key: "passportNumber", label: "Passport Number" },
          {
            key: "passportDateOfExpiry",
            label: "Passport Date Of Expiry",
            isDate: true,
            showFilePreview: true,
            fileName: "passportBIOS",
          },
        ],
      },
    };
  }

  static get FORM_FIELDS() {
    const contactPersonInfoFields = [
      { key: "companyContactName", label: "Full Name" },
      { key: "companyContactDesignation", label: "Designation" },
      { key: "telNumber", label: "Telephone Number" },
      { key: "emailAddress", label: "Email Address" },
    ];

    const individualsNamesFields = [
      { key: "firstName", label: "First name" },
      { key: "middleName", label: "Middle name" },
      { key: "lastName", label: "Last name" },
      { key: "formerName", label: "Former name" },
    ];

    const electronicAddressFields = [
      { key: "emailAddress", label: "Email address" },
      { key: "mobileNumber", label: "Mobile number" },
    ];

    return {
      "Kenyan Citizen": {
        names: individualsNamesFields,
        identificationDetails: [
          { key: "dateOfBirth", label: "Date of birth", isDate: true },
          {
            key: "nationalIDNumber",
            label: "National ID",
            showFilePreview: true,
            fileName: "nationalID",
          },
          {
            key: "KRAPin",
            label: "KRA PIN",
            showFilePreview: true,
            fileName: "KRACert",
          },
          { key: "occupation", label: "Occupation" },
          { key: "gender", label: "Gender" },
          { key: "nationality", label: "Nationality", isCountry: true },
        ],
        electronicAddress: electronicAddressFields,
        postalAddress: [
          { key: "postalAddress", label: "Postal Address" },
          { key: "postalCode", label: "Postal code" },
          { key: "serviceAddress", label: "Service address" },
        ],
        cessation: [
          { key: "cessationDate", label: "Cessation Date", isDate: true },
        ],
        appointment: [
          { key: "appointmentDate", label: "Appointment Date", isDate: true },
        ],
        residentialAddressDetails: [
          { key: "buildingName", label: "Building Name" },
          { key: "houseNumber", label: "House Number" },
          { key: "estate", label: "Estate" },
          { key: "floorNumber", label: "Floor Number" },
          { key: "streetName", label: "Street Name" },
          { key: "streetNumber", label: "Street Number" },
          { key: "county", label: "County" },
          { key: "district", label: "District" },
          { key: "locality", label: "Locality" },
        ],
      },
      "Foreign Resident": {
        names: individualsNamesFields,
        identificationDetails: [
          { key: "dateOfBirth", label: "Date of birth", isDate: true },
          {
            key: "foreignCertificateID",
            label: "Foreign Certificate ID",
            showFilePreview: true,
            fileName: "foreignerCert",
          },
          {
            key: "foreignCertificateDateOfExpiry",
            label: "Foreign Certificate Date Of Expiry",
            isDate: true,
            fileName: "foreignCertificateDateOfExpiry",
          },
          {
            key: "KRAPin",
            label: "KRA PIN",
            showFilePreview: true,
            fileName: "KRACert",
          },
          { key: "occupation", label: "Occupation" },
          { key: "nationality", label: "Nationality", isCountry: true },
          { key: "gender", label: "Gender" },
        ],
        cessation: [
          { key: "cessationDate", label: "Cessation Date", isDate: true },
        ],
        appointment: [
          { key: "appointmentDate", label: "Appointment Date", isDate: true },
        ],
        postalAddress: [
          { key: "postalAddress", label: "Postal Address" },
          { key: "postalCode", label: "Postal code" },
          { key: "serviceAddress", label: "Service address" },
        ],
        electronicAddress: electronicAddressFields,
        residentialAddressDetails: [
          { key: "buildingName", label: "Building Name" },
          { key: "houseNumber", label: "House Number" },
          { key: "estate", label: "Estate" },
          { key: "floorNumber", label: "Floor Number" },
          { key: "streetName", label: "Street Name" },
          { key: "streetNumber", label: "Street Number" },
          { key: "county", label: "County" },
          { key: "district", label: "District" },
          { key: "locality", label: "Locality" },
        ],
      },
      Foreigner: {
        names: individualsNamesFields,
        identificationDetails: [
          { key: "dateOfBirth", label: "Date of birth", isDate: true },
          { key: "passportNumber", label: "Passport Number" },
          {
            key: "passportDateOfExpiry",
            label: "Passport Date Of Expiry",
            isDate: true,
            showFilePreview: true,
            fileName: "passportBIOS",
          },
          { key: "occupation", label: "Occupation" },
          { key: "nationality", label: "Nationality", isCountry: true },
          { key: "gender", label: "Gender" },
        ],
        cessation: [
          { key: "cessationDate", label: "Cessation Date", isDate: true },
        ],
        appointment: [
          { key: "appointmentDate", label: "Appointment Date", isDate: true },
        ],
        electronicAddress: electronicAddressFields,
        postalAddress: [
          { key: "postalAddress", label: "Postal Address" },
          { key: "serviceAddress", label: "Service address" },
        ],
        residentialAddressDetails: [
          { key: "buildingName", label: "Building Name" },
          { key: "houseNumber", label: "House Number" },
          { key: "estate", label: "Estate" },
          { key: "floorNumber", label: "Floor Number" },
          { key: "streetName", label: "Street Name" },
          { key: "streetNumber", label: "Street Number" },
          { key: "city", label: "City" },
          { key: "country", label: "Country", isCountry: true },
        ],
      },
      Minor: {
        names: individualsNamesFields,
        identificationDetails: [
          {
            key: "birthCertificateNumber",
            label: "Birth Certificate Number",
            showFilePreview: true,
            fileName: "birthCertificate",
          },
          { key: "dateOfBirth", label: "Date of birth", isDate: true },
          { key: "gender", label: "Gender" },
          { key: "nationality", label: "Nationality", isCountry: true },
          { key: "guardianFullName", label: "Guardian Full Name" },
          { key: "guardianRelationship", label: "Guardian Relationship" },
          { key: "guardianMobileNumber", label: "Guardian Mobile Number" },
          { key: "guardianEmailAddress", label: "Guardian Email Address" },
        ],
        cessation: [
          { key: "cessationDate", label: "Cessation Date", isDate: true },
        ],
        appointment: [
          { key: "appointmentDate", label: "Appointment Date", isDate: true },
        ],
        electronicAddress: electronicAddressFields,
        postalAddress: [
          { key: "postalAddress", label: "Postal Address" },
          { key: "postalCode", label: "Postal code" },
          { key: "serviceAddress", label: "Service address" },
        ],
        residentialAddressDetails: [
          { key: "buildingName", label: "Building Name" },
          { key: "houseNumber", label: "House Number" },
          { key: "estate", label: "Estate" },
          { key: "floorNumber", label: "Floor Number" },
          { key: "streetName", label: "Street Name" },
          { key: "streetNumber", label: "Street Number" },
          { key: "county", label: "County" },
          { key: "district", label: "District" },
          { key: "locality", label: "Locality" },
        ],
      },
      "Local Company": {
        names: [
          { key: "companyName", label: "Company Name" },
          { key: "companyRegNumber", label: "Company Registration Number" },
          {
            key: "dateOfRegistration",
            label: "Company Registration Date",
            isDate: true,
          },
        ],
        identificationDetails: [
          {
            key: "companyKRAPIN",
            label: "Company's KRA PIN",
            showFilePreview: true,
            fileName: "KRACert",
          },
          { key: "companyFormerName", label: "Company Former Name" },
          {
            key: "countryOfIncorporation",
            label: "Country of Incorporation",
            isCountry: true,
            showFilePreview: true,
            fileName: "certOfIncorporation",
          },
          { key: "entityType", label: "Entity Type" },
        ],
        cessation: [
          { key: "cessationDate", label: "Cessation Date", isDate: true },
        ],
        appointment: [
          { key: "appointmentDate", label: "Appointment Date", isDate: true },
        ],
        contactPersonInfo: contactPersonInfoFields,
        postalAddress: [
          { key: "postalAddress", label: "Postal Address" },
          { key: "postalCode", label: "Postal code" },
          { key: "serviceAddress", label: "Service address" },
        ],
        electronicAddress: electronicAddressFields,
        residentialAddressDetails: [
          { key: "buildingName", label: "Building Name" },
          { key: "houseNumber", label: "House Number" },
          { key: "estate", label: "Estate" },
          { key: "floorNumber", label: "Floor Number" },
          { key: "streetName", label: "Street Name" },
          { key: "streetNumber", label: "Street Number" },
          { key: "county", label: "County" },
          { key: "district", label: "District" },
          { key: "locality", label: "Locality" },
        ],
      },
      "Foreign Company": {
        names: [
          { key: "companyName", label: "Company Name" },
          { key: "companyRegNumber", label: "Company Registration Number" },
          {
            key: "dateOfRegistration",
            label: "Company Registration Date",
            isDate: true,
          },
        ],
        identificationDetails: [
          {
            key: "countryOfIncorporation",
            label: "Country of Incorporation",
            isCountry: true,
            showFilePreview: true,
            fileName: "certOfIncorporation",
          },
          { key: "companyFormerName", label: "Company Former Name" },
          { key: "entityType", label: "Entity Type" },
        ],
        contactPersonInfo: contactPersonInfoFields,
        postalAddress: [
          { key: "postalAddress", label: "Postal Address" },
          { key: "serviceAddress", label: "Service address" },
        ],
        cessation: [
          { key: "cessationDate", label: "Cessation Date", isDate: true },
        ],
        appointment: [
          { key: "appointmentDate", label: "Appointment Date", isDate: true },
        ],
        electronicAddress: electronicAddressFields,
        residentialAddressDetails: [
          { key: "buildingName", label: "Building Name" },
          { key: "houseNumber", label: "House Number" },
          { key: "estate", label: "Estate" },
          { key: "floorNumber", label: "Floor Number" },
          { key: "streetName", label: "Street Name" },
          { key: "streetNumber", label: "Street Number" },
          { key: "city", label: "City" },
          { key: "country", label: "Country" },
        ],
      },
      "Other e.g Parastatals": {
        names: [
          { key: "companyName", label: "Company Name" },
          { key: "companyRegNumber", label: "Company Registration Number" },
          {
            key: "dateOfRegistration",
            label: "Company Registration Date",
            isDate: true,
          },
        ],
        identificationDetails: [
          { key: "companyKRAPIN", label: "Company's KRA PIN" },
          { key: "companyFormerName", label: "Company Former Name" },
          { key: "governingLaw", label: "Governing Law" },
          {
            key: "countryOfIncorporation",
            label: "Country of Incorporation",
            isCountry: true,
          },
          { key: "entityType", label: "Entity Type" },
        ],
        cessation: [
          { key: "cessationDate", label: "Cessation Date", isDate: true },
        ],
        appointment: [
          { key: "appointmentDate", label: "Appointment Date", isDate: true },
        ],
        contactPersonInfo: contactPersonInfoFields,
        postalAddress: [
          { key: "postalAddress", label: "Postal Address" },
          { key: "postalCode", label: "Postal code" },
          { key: "serviceAddress", label: "Service address" },
        ],
        electronicAddress: electronicAddressFields,
        residentialAddressDetails: [
          { key: "buildingName", label: "Building Name" },
          { key: "houseNumber", label: "House Number" },
          { key: "estate", label: "Estate" },
          { key: "floorNumber", label: "Floor Number" },
          { key: "streetName", label: "Street Name" },
          { key: "streetNumber", label: "Street Number" },
          { key: "county", label: "County" },
          { key: "district", label: "District" },
          { key: "locality", label: "Locality" },
        ],
      },
      "State Owned Enterprise": {
        names: [{ key: "fullName", label: "Full Name" }],
        identificationDetails: [],
      },
    };
  }

  static get COMPANY_NAME_SUFFICE() {
    return {
      PLC: "PUBLIC LIMITED COMPANY",
      PVT: "LIMITED",
    };
  }

  static get COMPANY_NAME_ABBREVIATION() {
    return {
      "Public Limited Company": "PLC",
      "Private Company": "PVT",
    };
  }

  static get PAYMENT_TRANSACTION_NEXT_STEPS() {
    return {
      PROCEED: "PROCEED",
      RETRY: "RETRY",
      REGENERATE: "REGENERATE",
      INITIATE: "INITIATE",
    };
  }

  static get REQUIRED_NAME_FIELDS() {
    return [
      "firstName",
      "lastName",
      "companyName",
      "companyRegNumber",
      "dateOfRegistration",
    ];
  }

  static get REQUIRED_IDENTIFICATION_FIELDS() {
    return [
      "dateOfBirth",
      "gender",
      "nationalIDNumber",
      "KRAPin",
      "occupation",
      "foreignCertificateID",
      "nationality",
      "passportNumber",
      "passportDateOfExpiry",
      "birthCertificateNumber",
      "guardianFullName",
      "guardianRelationship",
      "guardianMobileNumber",
      "guardianEmailAddress",
      "companyContactName",
      "companyContactDesignation",
      "telNumber",
      "emailAddress",
      "companyKRAPIN",
      "countryOfIncorporation",
      "governingLaw",
      "changeOfParticularDate",
    ];
  }

  static get REQUIRED_POSTAL_CODE_FIELDS() {
    return ["isNonResident", "postalAddress", "postalCode"];
  }

  static get REQUIRED_ELECTRONIC_ADDRESS_FIELDS() {
    return ["emailAddress", "companyEmailAddress", "mobileNumber"];
  }

  static get REQUIRED_RESIDENTIAL_FIELDS() {
    return [
      "buildingName",
      "estate",
      "city",
      "country",
      "county",
      "district",
      "locality",
    ];
  }

  static get REQUIRED_FILE_UPLOAD_FIELDS() {
    return [
      "passportPhoto",
      "passportBIOS",
      "KRACert",
      "foreignerCert",
      "practicingCert",
      "nationalID",
      "certOfIncorporation",
      "KRAPIN",
      "leadPartnerCertificate",
      "articlesOfAssociation",
      "birthCertificate",
      "gazetteNotice",
      "formCR1",
      "formCR2",
      "statementOfNominalCapital",
      "formCR8",
      "formCR10",
      "formCR12",
      "consentToAct",
    ];
  }

  static get REQUIRED_SHARE_ALLOCATION_FIELDS() {
    return ["classOfShares", "allottedShares", "paidUpShares", "sharePrice"];
  }

  static get REQUIRED_OFFICIALS_FIELDS() {
    return [
      ...this.REQUIRED_NAME_FIELDS,
      ...this.REQUIRED_IDENTIFICATION_FIELDS,
      ...this.REQUIRED_POSTAL_CODE_FIELDS,
      ...this.REQUIRED_ELECTRONIC_ADDRESS_FIELDS,
      ...this.REQUIRED_RESIDENTIAL_FIELDS,
      ...this.REQUIRED_SHARE_ALLOCATION_FIELDS,
    ];
  }

  static get DELAY_FORM_SUBMISSION() {
    return 1000;
  }

  static get MAX_COMMENT_LENGTH() {
    return 1000;
  }

  // static get REVIEW_TRANSACTIONS_AGAINST_DESIGNATIONS() {
  //   return {
  //     REVIEW_TRANSACTIONS: {
  //       SHAREHOLDER: {
  //         TYPEOFDESIGNATION: {
  //           SHAREHOLDER: [],
  //           JOINT_SHAREHOLDER: [],
  //           DIRECTOR_SHAREHOLDER: [],
  //         },
  //       },
  //       DIRECTOR: {
  //         DIRECTOR: [],
  //         DIRECTOR_SHAREHOLDER: [],
  //       },
  //     }
  //   }
  // }

  // static get CHANGED_SCENARIOS_APPROVAL_PROCESS() {
  //   return {
  //     APPOINTMENT_OF_AUTHORIZED_PERSON: {
  //       REVIEWER: "DIRECTOR",
  //       APPROVER: "DIIRECTOR",
  //     }
  //   }
  // }

  static get COOKIE_ROLES() {
    return {
      APPLICANT: "7",
      REVIEWER: "8",
      SUPER_ADMIN: "9",
      SYSTEM_ADMIN: "11",
      ORG_ADMIN: "13",
      OFFICIAL: "6",
      CONTACT_PERSON: "14",
      CO_APPLICANT: "15",
      BRS_OPS: "17",
    };
  }

  static get ROLES() {
    return {
      OFFICIAL: 6,
      APPLICANT: 7,
      REVIEWER: 8,
      SUPER_ADMIN: 9,
      SYSTEM_ADMIN: 11,
      ORG_ADMIN: 13,
      CO_APPLICANT: 15,
      CORPORATE_ORG_ADMIN: 12,
      BRS_OPS: 17,
    };
  }

  static get SFL_SERVICES() {
    return [
      { label: "Public Limited Company", value: "Public Limited Company" },
      { label: "Private Limited Company", value: "Private Limited Company" },
    ];
  }

  static get PVT_PACKAGE_NAMES() {
    return [
      { label: "Basic", value: "Basic" },
      { label: "Print", value: "Print" },
      { label: "Premium", value: "Premium" },
    ];
  }

  static get PLC_PACKAGE_NAMES() {
    return [
      { label: "Print", value: "Print" },
      { label: "Premium", value: "Premium" },
    ];
  }

  static get SUBSCRIPTION_PACKAGE_NAMES() {
    return {
      SUPER_ADMIN: [
        {
          label: "Essentials",
          value: "Essentials",
        },
        {
          label: "Plus",
          value: "Plus",
        },
        {
          label: "Pro",
          value: "Pro",
        },
      ],
      ORG_ADMIN: [
        { label: "Basic", value: "Basic" },
        { label: "Print", value: "Print" },
        { label: "Premium", value: "Premium" },
      ],
    };
  }

  static get SUBSCRIPTION_PACKAGE_NAMES_OBJECT() {
    return Object.freeze({
      ESSENTIALS: "Essentials",
      PRO: "Pro",
      PLUS: "Plus",
      BASIC: "Basic",
      PRINT: "Print",
      PREMIUM: "Premium",
    });
  }

  static get CURRENCIES() {
    return [
      { label: "KES", value: "KES" },
      { label: "USD", value: "USD" },
    ];
  }

  static get APPLICABLE_TAX_OPTIONS() {
    return [
      { label: "16 %", value: 1.16 },
      { label: "N/A", value: 1 },
    ];
  }

  static get SFL_DATA() {
    return {
      name: "SIMPLE FORMATIONS LIMITED",
      buildingName: "The Address",
      floorNumber: "8th Floor",
      streetName: "Muthangari Drive Off Waiyaki way",
      pin: "P051793526K",
      location: {
        county: "Nairobi",
      },
    };
  }

  static get PHONE_NUMBER_REGEX() {
    return /^((\+[1-9]{1,4}[ -]?)|(\([0-9]{2,3}\)[ -]?)|([0-9]{2,4})[ -]?)*?[0-9]{3,4}[ -]?[0-9]{3,4}$/;
  }

  static get DPO() {
    return {
      SERVICE_TYPES: {
        // COMPANY_REGISTRATION: 77416, // old
        // COMPANY_REGISTRATION: 54841, // redirect checkout test
        COMPANY_REGISTRATION: 77416,
        REGISTRATION_FEE: 80295,
      },
      CHECKOUT_LINK: "https://secure.3gdirectpay.com/payv3.php",
    };
  }

  static get MPESA_LIMIT() {
    return { MAX_AMOUNT: 150000 };
  }

  static get SOURCE_OF_BO_INFO() {
    return {
      officialRegister: "Taken from an official register",
      selfOrAuthorizedPerson:
        "Provided by the beneficial owner or their authorized representative",
      thirdParty:
        "Provided by a third party not directly related to the beneficial owner",
    };
  }

  static get TEMPLATES() {
    return {
      ENTITY_TYPES: [
        { label: "PLC", value: "PLC" },
        { label: "PVT", value: "PVT" },
      ],
      TYPES: {
        ARTICLES: [
          { label: "Short", value: "Short" },
          { label: "Long", value: "Long" },
          { label: "Model", value: "Model" },
        ],
        SHARES_RIGHTS: [
          { value: this.ORDINARY_SHARES, label: this.ORDINARY_SHARES },
          {
            value: this.PREFERENCE_SHARES,
            label: this.PREFERENCE_SHARES,
          },
          {
            value: this.OTHER_SHARES,
            label: this.OTHER_SHARES,
          },
        ],
      },
      DIRECTOR_MUST_BE_SHAREHOLDER: [
        { label: "Yes", value: "Yes" },
        { label: "No", value: "No" },
      ],
      BORROWING_POWERS_FOR_DIRECTORS: [
        { label: "Limited Powers", value: "Limited Powers" },
        { label: "Unlimited Powers", value: "Unlimited Powers" },
      ],
      SHAREHOLDER_RIGHT_OF_FIRST_REFUSAL: [
        { label: "Apply", value: "Apply" },
        { label: "Do Not Apply", value: "Do Not Apply" },
      ],
    };
  }

  static get ORDINARY_SHARES() {
    return "Ordinary Shares";
  }
  static get PREFERENCE_SHARES() {
    return "Preference Shares";
  }
  static get OTHER_SHARES() {
    return "Other Shares";
  }

  static get ENTITY_TYPE_AGAINST_TYPE() {
    return {
      PLC: "Public Limited",
      PVT: "Private Limited",
    };
  }

  static get ENTITY_STATUS() {
    return {
      ACTIVE: "Active",
      STRIKE_OFF_ONGOING: "Strike Off Ongoing",
      STRUCK_OFF: "Struck Off",
      DRAFT: "Draft",
    };
  }

  static get ENTITY_STRIKE_APPLICATION_OPTIONS() {
    return {
      COMPANY_APPLICATION_FOR_STRIKE_OFF: "Company Application for Strike Off",
      CREDITORS_LIQUIDATION: "Creditors Liquidation",
      COURT_LIQUIDATION: "Court Liquidation",
    };
  }

  static get SFL_URL() {
    return "https://www.simpleformations.ke/";
  }

  static get SFL_APP_URL() {
    return "https://www.app.simpleformations.ke";
  }

  static get PAGINATION_LIMIT() {
    return 15;
  }

  static get ACCESS_REQUEST() {
    return {
      PERSONAL_DATA: "Personal Data",
      CORPORATE_DATA: "Corporate Data",
      MINOR_DATA: "Minor Data",
      CO_APPLICANT: "Co Applicant",
    };
  }

  static get INACTIVITY_TIMEOUT_MS() {
    return 15 * 60 * 1000; // 15 minutes
  }

  static get INACTIVE_STATE_CHECK_INTERVAL() {
    return 1000; // milliseconds
  }

  static get ID_TYPES() {
    return {
      KENYAN_CITIZEN: "Kenyan Citizen",
      FOREIGN_RESIDENT: "Foreign Resident",
      FOREIGNER: "Foreigner",
      MINOR: "Minor",
      LOCAL_COMPANY: "Local Company",
      FOREIGN_COMPANY: "Foreign Company",
      OTHER_PARASTATALS: "Other e.g Parastatals",
    };
  }

  static get INDIVIDUALS_ID_TYPES() {
    return Object.freeze([
      "Kenyan Citizen",
      "Foreign Resident",
      "Foreigner",
      "Minor",
      "Estate",
    ]);
  }

  static get CORPORATES_ID_TYPES() {
    return Object.freeze([
      "Local Company",
      "Foreign Company",
      "Other e.g Parastatals",
    ]);
  }

  static get SOURCE_OF_BO_INFO_OPTIONS() {
    return [
      {
        value: "selfOrAuthorizedPerson",
        label:
          "Provided by the beneficial owner or their authorized representative",
      },
      {
        value: "officialRegister",
        label: "Taken from an official register",
      },
      {
        value: "thirdParty",
        label:
          "Provided by a third party not directly related to the beneficial owner",
      },
    ];
  }

  static get REGISTERS() {
    return {
      ROW_HEIGHT: 26,
      DIRECTORS_ROWS: 17,
      SECRETARIES_ROWS: 4,
      BENEFICIAL_OWNERS_ROWS: 3,
      MEMBER_AND_LEDGER_ROWS: 2,
      APPLICATIONS_AND_ALLOTMENT_ROWS: 17,
      LEDGER_ROWS: 7,
    };
  }

  static get SOCKET_EVENTS() {
    return {
      FETCH_ACCESS_REQUESTS: "fetch-access-requests",
      UPDATE_REGISTERED_OFFICE: "update-registered-office",
      FETCH_CO_APPLICANTS: "fetch-co-applicants",
      ADD_COMPANY_OFFICIAL: "add-company-official",
      REFETCH_BENEFICIAL_OWNER: "refetch-beneficial-owner",
    };
  }

  static get APPLICANT_CAPACITIES() {
    return {
      DIRECTOR: "Director",
      DIRECTOR_SHAREHOLDER: "Director Shareholder",
      SHAREHOLDER: "Shareholder",
      BENEFICIAL_OWNER: "Beneficial Owner",
      COMPANY_SECRETARY: "Company Secretary",
      ADVOCATE: "Advocate",
      AUTHORIZED_PERSON: "Authorized Person",
      JOINT_SHAREHOLDER: "Joint Shareholder",
      ALTERNATE_DIRECTOR: "Alternate Director",
      ADMINISTRATOR: "Administrator",
      AUDITOR: "Auditor",
    };
  }

  static get PROFILE_TYPES() {
    return {
      EMPLOYEE: "Employee",
      CO_APPLICANT: "Co Applicant",
      APPLICANT: "Applicant",
      FULL: "Full",
      PROFESSIONAL: "Professional",
    };
  }
  static get Template_FILE_TYPES() {
    return {
      CONSENT_TO_ACT: "ConsentToAct",
      APPOINTMENT_LETTER: "AppointmentLetter",
      DIRECTOR_RESIGNATION_LETTER: "DirectorResignationLetter",
      SHAREHOLDERS_SPECIAL_NOTICE: "ShareholdersSpecialNotice",
      DEATH_CERTIFICATE: "DeathCertificate",
      DIRECTOR_STATUTORY_DECLARATION: "DirectorStatutoryDeclaration",
      INSOLVENCY_ORDER: "InsolvencyOrder",
      MEDICAL_REPORT: "MedicalReport",
      BOD_RESOLS_CHANGES: "BoDResolutionsChanges",
      RESIGNATION_LETTER: "ResignationLetter",
      ADDRESS_STATUTORY_DECLARATION: "addressStatutoryDeclaration",
      NOTICE_OF_REMOVAL_BY_APPOINTOR: "NoticeOfRemovalByAppointor",
      SHAREHOLDER_APPROVAL: "ShareholderApproval",
      COURT_ORDER: "ownConsentResolutionForCourtOrderApproval",
      GAZETTE_NOTICE: "ownConsentResolutionForGazetteApproval",
      CREDITORS_RESOLUTION: "ownConsentResolutionForCreditorsApproval",
      STATUTORY_DECLARATION: "StatutoryDeclaration",
    };
  }

  static get MAINTAIN_COMPANY_REVIEW_STATUSES() {
    return {
      DRAFT: "DRAFT",
      ACTIVE: "ACTIVE",
      REMOVE: "REMOVE",
      RESIGNATION: "RESIGNATION",
      DIRECTOR_APPROVAL: "DIRECTOR-APPROVAL",
      SHAREHOLDER_APPROVAL: "SHAREHOLDER-APPROVAL",

      REGISTRAR_REVIEW: "REGISTRAR-REVIEW",
      REGISTRAR_CORRECTIONS: "REGISTRAR-CORRECTIONS",
      REGISTRAR_REVIEW_II: "REGISTRAR-REVIEW-II",
      REGISTRAR_APPROVAL: "REGISTRAR-APPROVAL",

      LEGAL_REVIEW: "LEGAL-REVIEW",
      LEGAL_REVIEW_II: "LEGAL-REVIEW-II",
      LEGAL_CORRECTIONS: "LEGAL-CORRECTIONS",
      LEGAL_APPROVAL: "LEGAL-APPROVAL",

      LEGAL_SIGNATURE_SETUP: "LEGAL-SIGNATURE-SETUP",
      SIGNATURE_SETUP: "SIGNATURE-SETUP",

      CHANGES_SIGNATURE: "CHANGES-SIGNATURE",
      CHANGES_SIGNATURE_CORRECTIONS: "CHANGES-SIGNATURE-CORRECTIONS",
      CHANGES_SIGNATURE_II: "CHANGES-SIGNATURE-II",

      REGISTRAR_CS_REVIEW: "REGISTRAR-CS-REVIEW",
      REGISTRAR_CS_REVIEW_II: "REGISTRAR-CS-REVIEW-II",
      REGISTRAR_CS_CORRECTIONS: "REGISTRAR-CS-CORRECTIONS",
      REGISTRAR_CS_APPROVAL: "REGISTRAR-CS-APPROVAL",

      DIRECTOR_REVIEW: "DIRECTOR-REVIEW",
      DIRECTOR_REVIEW_II: "DIRECTOR-REVIEW-II",
      DIRECTOR_CORRECTIONS: "DIRECTOR-CORRECTIONS",
      DIRECTOR_REVIEW_APPROVAL: "DIRECTOR-REVIEW-APPROVAL",

      DIRECTOR_SIGNATURE: "DIRECTOR-SIGNATURE",
      DIRECTOR_SIGNATURE_CORRECTIONS: "DIRECTOR-SIGNATURE-CORRECTIONS",
      DIRECTOR_SIGNATURE_II: "DIRECTOR-SIGNATURE-II",

      REGISTRAR_DR_REVIEW: "REGISTRAR-DR-REVIEW",
      REGISTRAR_DR_REVIEW_II: "REGISTRAR-DR-REVIEW-II",
      REGISTRAR_DR_CORRECTIONS: "REGISTRAR-DR-CORRECTIONS",
      REGISTRAR_DR_APPROVAL: "REGISTRAR-DR-APPROVAL",

      SHAREHOLDER_REVIEW: "SHAREHOLDER-REVIEW",
      SHAREHOLDER_REVIEW_II: "SHAREHOLDER-REVIEW-II",
      SHAREHOLDER_CORRECTIONS: "SHAREHOLDER-CORRECTIONS",
      SHAREHOLDER_REVIEW_APPROVAL: "SHAREHOLDER-REVIEW-APPROVAL",

      SHAREHOLDER_SIGNATURE: "SHAREHOLDER-SIGNATURE",
      SHAREHOLDER_SIGNATURE_CORRECTIONS: "SHAREHOLDER-SIGNATURE-CORRECTIONS",
      SHAREHOLDER_SIGNATURE_II: "SHAREHOLDER-SIGNATURE-II",

      REGISTRAR_SH_REVIEW: "REGISTRAR-SH-REVIEW",
      REGISTRAR_SH_REVIEW_II: "REGISTRAR-SH-REVIEW-II",
      REGISTRAR_SH_CORRECTIONS: "REGISTRAR-SH-CORRECTIONS",
      REGISTRAR_SH_APPROVAL: "REGISTRAR-SH-APPROVAL",

      ADMIN_REVIEW: "ADMIN-REVIEW",
      ADMIN_CORRECTIONS: "ADMIN-CORRECTIONS",
      ADMIN_REVIEW_II: "ADMIN-REVIEW-II",
      ADMIN_APPROVAL: "ADMIN-APPROVAL",

      SUBMISSION: "SUBMISSION",
      SUBMISSION_APPROVAL: "SUBMISSION-APPROVAL",
      SUBMISSION_CORRECTIONS: "SUBMISSION-CORRECTIONS",
      SUBMISSION_II: "SUBMISSION-II",

      BRS_FORM_SIGNATURE: "BRS-FORM-SIGNATURE",
      BRS_FORM_SIGNATURE_CORRECTION: "BRS-FORM-SIGNATURE-CORRECTION",
      BRS_FORM_SIGNATURE_II: "BRS-FORM-SIGNATURE-II",

      REGISTRAR_BF_REVIEW: "REGISTRAR-BF-REVIEW",
      REGISTRAR_BF_APPROVAL: "REGISTRAR-BF-APPROVAL",

      BRS_REVIEW: "BRS-REVIEW",
      BRS_APPROVAL: "BRS-APPROVAL",
      BRS_CORRECTIONS: "BRS-CORRECTIONS",
      BRS_REVIEW_II: "BRS-REVIEW-II",

      CANCELLATION_NOTICE: "CANCELLATION-NOTICE",
      CANCELLATION_NOTICE_CORRECTION: "CANCELLATION-NOTICE-CORRECTION",
      CANCELLATION_NOTICE_II: "CANCELLATION-NOTICE-II",

      AWAITING_COURT_ORDER: "AWAITING-COURT-ORDER",

      INTENDED_DISSOLUTION: "INTENDED-DISSOLUTION",
      DISSOLUTION_NOTICE: "DISSOLUTION-NOTICE",

      PENDING_REGISTRAR_DECISION: "PENDING-REGISTRAR-DECISION",

      REJECTED: "REJECTED",
      CANCELLED: "CANCELLED",
      DEFERRED: "DEFERRED",
      VETTING: "VETTING",
      PROCESSED: "PROCESSED",
    };
  }
  static get MAINTAIN_COMPANY_REVIEW_PROCESS() {
    return {
      DIRECTOR_APPROVAL: "DIRECTOR-APPROVAL",
      SHAREHOLDER_APPROVAL: "SHAREHOLDER-APPROVAL",
      REGISTRAR_APPROVAL: "REGISTRAR-APPROVAL",
      LEGAL_APPROVAL: "LEGAL-APPROVAL",
      REGISTRAR_CS_REVIEW: "REGISTRAR-CS-REVIEW",
      REGISTRAR_CS_APPROVAL: "REGISTRAR-CS-APPROVAL",
      DIRECTOR_REVIEW_II: "DIRECTOR-REVIEW-II",
      REGISTRAR_DR_REVIEW: "REGISTRAR-DR-REVIEW",
      REGISTRAR_REVIEW: "REGISTRAR-REVIEW",
      REGISTRAR_REVIEW_II: "REGISTRAR-REVIEW-II",
    };
  }
  static get MAINTAIN_COMPANY_CORRECTIONS_STATUSES() {
    return {
      REGISTRAR_CORRECTION: "REGISTRAR-CORRECTION",
      LEGAL_CORRECTION: "LEGAL-CORRECTION",
      CHANGES_SIGNATURE_CORRECTIONS: "CHANGES-SIGNATURE-CORRECTIONS",
      DIRECTOR_CORRECTIONS: "DIRECTOR-CORRECTIONS",
      DIRECTOR_SIGNATURE_CORRECTIONS: "DIRECTOR-SIGNATURE-CORRECTIONS",
      SHAREHOLDER_CORRECTIONS: "SHAREHOLDER-CORRECTIONS",
      SHAREHOLDER_SIGNATURE_CORRECTIONS: "SHAREHOLDER-SIGNATURE-CORRECTIONS",
      BRS_FORM_SIGNATURE_CORRECTION: "BRS-FORM-SIGNATURE-CORRECTION",
      BRS_REVIEW_CORRECTION: "BRS-REVIEW-CORRECTION",
      ADMIN_CORRECTIONS: "ADMIN-CORRECTIONS",
    };
  }
  static get MAINTAIN_COMPANY_DRAFT_STATUS() {
    return {
      DRAFT: "DRAFT",
    };
  }
  static get MAINTAIN_COMPANY_SIGNATURE_STATUS() {
    return {
      CHANGES_SIGNATURE: "CHANGES-SIGNATURE",
      CHANGES_SIGNATURE_II: "CHANGES-SIGNATURE-II",
      BRS_FORM_SIGNATURE: "BRS-FORM-SIGNATURE",
      BRS_FORM_SIGNATURE_II: "BRS-FORM-SIGNATURE-II",
      DIRECTOR_SIGNATURE: "DIRECTOR-SIGNATURE",
      DIRECTOR_SIGNATURE_II: "DIRECTOR-SIGNATURE-II",
      SHAREHOLDER_SIGNATURE: "SHAREHOLDER-SIGNATURE",
      SHAREHOLDER_SIGNATURE_II: "SHAREHOLDER-SIGNATURE-II",
    };
  }
  static get MAINTAIN_COMPANY_SUBMITTED_STATUSES() {
    return ["ACTIVE", "DEFERRED", "PROCESSED", "VETTING"];
  }
  static get MAINTAIN_COMPANY_SIGNATURE_WORKFLOW() {
    return [
      this.MAINTAIN_COMPANY_REVIEW_STATUSES.DIRECTOR_APPROVAL,
      this.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_APPROVAL,
      this.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_REVIEW,
      this.MAINTAIN_COMPANY_REVIEW_STATUSES.LEGAL_APPROVAL,
      this.MAINTAIN_COMPANY_REVIEW_STATUSES.LEGAL_REVIEW,
      this.MAINTAIN_COMPANY_REVIEW_STATUSES.CHANGES_SIGNATURE_CORRECTIONS,
      this.MAINTAIN_COMPANY_REVIEW_STATUSES.DIRECTOR_SIGNATURE_CORRECTIONS,
    ];
  }
  static get RECALL_STATUSES() {
    return [
      "DIRECTOR-APPROVAL",
      "SHAREHOLDER-APPROVAL",
      "REGISTRAR-CORRECTION",
      "REGISTRAR-REVIEW-II",
      "DIRECTOR-REVIEW-CORRECTIONS",
      "DIRECTOR-REVIEW-II",
      "SHAREHOLDER-REVIEW-CORRECTION",
      "SHAREHOLDER-REVIEW-II",
      "LEGAL-REVIEW-CORRECTION",
      "LEGAL-REVIEW-II",
      "ADMIN-CORRECTIONS",
      "ADMIN-REVIEW-II",
      "BRS-REVIEW-CORRECTION",
      "BRS-REVIEW-II",
    ];
  }
  static get MAINTAIN_COMPANY_SHAREHOLDERS_DESIGNATIONS() {
    return [
      this.APPLICANT_CAPACITIES.BENEFICIAL_OWNER,
      this.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER,
      this.APPLICANT_CAPACITIES.SHAREHOLDER,
      this.APPLICANT_CAPACITIES.JOINT_SHAREHOLDER,
    ];
  }

  static get TRANSACTION_TYPES() {
    return {
      MANUAL: "MANUAL",
      AUTOMATIC: "AUTOMATIC",
    };
  }

  static get UPDATE_COMPANY_CHANGE_TYPES_SELECTIONS() {
    return {
      summary: [
        {
          id: "companyChanges",
          label: "Company Changes",
          options: [
            { id: "changeOfCompanyName", label: "Change of Company Name" },
            {
              id: "accountingReferenceDate",
              label: "Accounting Reference Date",
            },
            { id: "strikeOffApplication", label: "Strike Off Application" },
            { id: "conversionPVTtoPLC", label: "Conversion (PVT to PLC)" },
            { id: "conversionPLCtoPVT", label: "Conversion (PLC to PVT)" },
            {
              id: "noticeToCancelConversionPLCtoPVT",
              label:
                "Notice of application to Court to cancel Conversion of PLC to PVT",
            },
            {
              id: "noticeToCancelConversionPVTtoPLC",
              label:
                "Notice of application to Court to cancel Conversion of PVT to PLC",
            },
            {
              id: "noticeOfWithdrawalOfStrikeOffApplication",
              label: "Notice of withdrawal of Strike Off Application",
            },
            {
              id: "applicationForAdministrativeRestoration",
              label: "Application for administrative restoration",
            },
          ],
        },
        {
          id: "governanceChanges",
          label: "Governance Changes",
          options: [
            { id: "amendmentOfArticles", label: "Amendment of Articles (AoA)" },
          ],
        },
        {
          id: "securities",
          label: "Securities",
          options: [
            {
              id: "debentureAndChargesRegistration",
              label: "Debenture & Charges-Registration",
            },
            {
              id: "debentureAndChargesAmendment",
              label: "Debenture & Charges-Amendment",
            },
            {
              id: "debentureAndChargesDischarge",
              label: "Debenture & Charges-Discharge",
            },
          ],
        },
        {
          id: "annualCompliance",
          label: "Annual Compliance",
          options: [
            { id: "annualReturns", label: "Annual Returns" },
            { id: "financialStatements", label: "Financial Statements" },
          ],
        },
      ],
      registeredAddress: [
        {
          id: "changeOfAddress",
          label: "Change of Address Details",
          options: [
            {
              id: "changeOfRegisteredAddress",
              label: "Change of Registered Address",
            },
          ],
        },
      ],
      shareInformation: [
        {
          id: "capitalIncrease",
          label: "Capital Increase",
          options: [
            {
              id: "privateWithMoreThanOneClass",
              label: "Authorization and Allotment",
            },
            { id: "authorization", label: "Authorization" },
            {
              id: "unissuedShare/PVTwithOneClass",
              label: "Unissued Share/PVT with One Class",
            },
            { id: "PVTwithOneClass", label: "PVT with One Class" },
            {
              id: "unissuedShare/existingAuthorization",
              label: "Unissued Share/Existing Authorization",
            },
            { id: "unissuedShare", label: "Unissued Share" },
            { id: "existingAuthorization", label: "Existing Authorization" },
            // { id: 'unissuedShare/exisitingAuthorization/PVTwithOneClass', label: 'Unissued Share/Existing Authorization/PVT with One Class' },
          ],
        },
        {
          id: "shareholdingChanges",
          label: "Shareholding Changes",
          options: [
            { id: "shareTransfer", label: "Share Transfer" },
            { id: "transferShares", label: "Transmission of Shares" },
            { id: "forfeitureOfShares", label: "Forfeiture of Shares" },
            { id: "surrenderOfShares", label: "Surrender of Shares" },
            { id: "sharesBuyback", label: "Shares Buyback" },
            {
              id: "cancelationOfTreasuryShares",
              label: "Cancelation of Treasury Shares",
            },
            {
              id: "sale/TransferOfTreasuryShares",
              label: "Sale/Transfer of Treasury Shares",
            },
            { id: "sharesRedemption", label: "Shares Redemption" },
            {
              id: "noticetoCourtToCancelSpecialSharesResolutionRedemption",
              label:
                "Notice to Court to cancel Special Resolution re Shares Redemption",
            },
            { id: "takeoverOffer", label: "Takeover Offer" },
          ],
        },
        {
          id: "sharesManagement",
          label: "Shares Management",
          options: [
            { id: "sharesConsolidation", label: "Shares Consolidation" },
            { id: "sharesSubdivision", label: "Shares Sub-Division" },
            {
              id: "noticeOfChangeOfNameOrOtherDesignationOfClassOfShares",
              label:
                "Notice of change of name or other designation of class of shares",
            },
            {
              id: "noticeOfChangeOfValuationOfClassRights",
              label: "Notice of change of valuation of class rights",
            },
          ],
        },
        {
          id: "capitalReduction",
          label: "Capital Reduction",
          options: [
            { id: "solvencyStatement", label: "Solvency Statement" },
            { id: "courtOrder", label: "Court Order" },
          ],
        },
      ],
      stakeholderDetails: [
        {
          id: "appointment",
          label: "Appointment",
          options: Object.values(Constants.APPLICANT_CAPACITIES)
            .filter(
              (capacity) => !EXCLUDED_APPLICANT_CAPACITIES.includes(capacity)
            )
            .map((capacity) => ({
              id: capacity,
              label:
                capacity === Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER
                  ? `Appointment of ${Constants.APPLICANT_CAPACITIES.SHAREHOLDER} as ${capacity}`
                  : `Appointment of ${capacity}`,
            })),
        },
        {
          id: "changeOfParticulars",
          label: "Change of Particulars",
          options: Object.values(Constants.APPLICANT_CAPACITIES)
            .filter(
              (capacity) =>
                !EXCLUDED_APPLICANT_CAPACITIES_FOR_CHANGE_OF_PARTICULARS.includes(
                  capacity
                )
            )
            .map((capacity) => ({
              id: capacity,
              label: `Change of Particulars of ${capacity}`,
            })),
        },
        {
          id: "cessation",
          label: "Cessation",
          options: Object.values(Constants.APPLICANT_CAPACITIES)
            .filter(
              (capacity) => !EXCLUDED_APPLICANT_CAPACITIES.includes(capacity)
            )
            .map((capacity) => ({
              id: capacity,
              label:
                capacity === Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER
                  ? `Cessation from ${capacity} to ${Constants.APPLICANT_CAPACITIES.SHAREHOLDER}`
                  : `Cessation of ${capacity}`,
            })),
        },
      ],
      beneficialOwners: [
        {
          id: "changesBeneficialOwners",
          label: "Changes in Beneficial Owners",
          options: [
            {
              id: Constants.APPLICANT_CAPACITIES.BENEFICIAL_OWNER,
              label: "Change of Particulars of Beneficial Owner",
            },
          ],
        },
      ],
    };
  }

  static get TRANSACTION_CHANGE_TYPES() {
    return {
      /** Officials Changes */
      APPOINTMENT: "APPOINTMENT",
      CESSATION: "CESSATION",
      CHANGE_OF_PARTICULARS: "CHANGE-OF-PARTICULARS",

      /** Shares Changes */
      SHARE_TRANSFER: "SHARE-TRANSFER",
      TRANSMISSION_OF_SHARES: "TRANSMISSION-OF-SHARES",
      FORFEITURE_OF_SHARES: "FORFEITURE-OF-SHARES",
      SURRENDER_OF_SHARES: "SURRENDER-OF-SHARES",
      SHARES_BUYBACK: "SHARES-BUYBACK",
      CANCELATION_OF_TREASURY_SHARES: "CANCELATION-OF-TREASURY-SHARES",
      SALE_TRANSFER_OF_TREASURY_SHARES: "SALE-TRANSFER-OF-TREASURY-SHARES",
      SHARES_REDEMPTION: "SHARES-REDEMPTION",
      NOTICE_TO_COURT: "NOTICE-TO-COURT",
      TAKEOVER_OFFER: "TAKEOVER-OFFER",
      SHARES_CONSOLIDATION: "SHARES-CONSOLIDATION",
      SHARES_SUBDIVISION: "SHARES-SUBDIVISION",
      NOTICE_OF_CHANGE_OF_NAME: "NOTICE-OF-CHANGE-OF-NAME",
      NOTICE_OF_PARTICULARS: "NOTICE-OF-PARTICULARS",
      SOLVENCY_STATEMENT: "SOLVENCY-STATEMENT",
      COURT_ORDER: "COURT-ORDER",
      AUTHORIZATION: "AUTHORIZATION",
      ALLOTMENT: "ALLOTMENT",
      AUTHORIZATION_AND_ALLOTMENT: "AUTHORIZATION-AND-ALLOTMENT",

      /** Company Changes */
      CHANGE_OF_REGISTERED_ADDRESS: "CHANGE-OF-REGISTERED-ADDRESS",
      CHANGE_OF_ARTICLES: "CHANGE-OF-ARTICLES", // governance
      CHANGE_OF_NAME: "CHANGE-OF-NAME", //1st
      CHANGE_OF_FY: "CHANGE-OF-FY", //last
      STRIKE_OFF: "STRIKE-OFF",
      CONVERSION: "CONVERSION", //1st Entity Type
      YYYY_ANNUAL_RETURNS: "YYYY-ANNUAL-RETURNS",
      YYYY_FINANCIAL_STATEMENTS: "YYYY-FINANCIAL-STATEMENTS",
      NOTICE_TO_CANCEL_CONVERSION: "NOTICE-TO-CANCEL-CONVERSION",
      NOTICE_OF_WITHDRAWAL_OF_STRIKE_OFF: "NOTICE-OF-WITHDRAWAL-OF-STRIKE-OFF",
      ADMINISTRATIVE_RESTORATION: "ADMINISTRATIVE-RESTORATION",
      DEBENTURE_AND_CHARGES_REGISTRATION: "DEBENTURE-AND-CHARGES-REGISTRATION",
      DEBENTURE_AND_CHARGES_AMENDMENT: "DEBENTURE-AND-CHARGES-AMENDMENT",
      DEBENTURE_AND_CHARGES_DISCHARGE: "DEBENTURE-AND-CHARGES-DISCHARGE",
    };
  }

  static get TRANSACTION_APPLICATION_TYPES() {
    return {
      /** Company Application Types */
      CHANGE_REGISTRATION: "CHANGE-REGISTRATION",
      ANNUAL_RETURNS: "ANNUAL-RETURNS",
      FINANCIAL_STATEMENTS: "FINANCIAL-STATEMENTS",

      /** Share capital application types */
      SHARE_CAPITAL: "Share Capital",
    };
  }

  static get TRANSACTION_APPROVAL_TYPES() {
    return {
      BOARD_APPROVAL: "boardApproval",
      SHAREHOLDER_APPROVAL: "shareholderApproval",
      BOARD_SHAREHOLDER_APPROVAL: "boardShareholderApproval",
      BOARD_CREDITORS_APPROVAL: "boardCreditorsApproval",
      BOARD_COURT_APPROVAL: "boardCourtApproval",
      COURT_ORDER: "courtOrder",
      CREDITORS_RESOLUTION: "creditorsResolution",
      GAZETTE_NOTICE: "gazetteNotice",
    };
  }

  static get COMPANY_REVIEW_STATUSES() {
    return {
      DRAFT: "Draft",

      /**Directors */
      DIRECTORS_REVIEW: "Directors Review",
      DIRECTORS_DEFERRED: "Directors Deferred",
      DIRECTORS_CORRECTIONS: "Directors Corrections",

      /**Registrar */
      REGISTRAR_REVIEW: "Registrar Review",
      REGISTRAR_DEFERRED: "Registrar Deferred",
      REGISTRAR_REVIEW_II: "Registrar Review II",
      REGISTRAR_APPROVAL: "Registrar Approval",

      /** Admin */
      ADMIN_REVIEW: "Admin Review",
      ADMIN_APPROVAL: "Admin Approval",
      ADMIN_DEFERRED: "Admin Deferred",
      ADMIN_REVIEW_II: "Admin Review II",
      ADMIN_CORRECTIONS: "Admin Corrections",

      /** Brs Ops | Super Admin */
      SUBMISSION: "Submission",
      SUBMISSION_DEFERRED: "Submission Deferred",
      SUBMISSION_CORRECTIONS: "Submission Corrections",
      SUBMISSION_APPROVAL: "Subsmission Approval",
      SUBMISSION_II: "Submission II",
      SIGNATURE: "Signature",
      SIGNATURE_II: "Signature II",
      SIGNATURE_CORRECTIONS: "Signature Corrections",
      SIGNATURE_DEFERRED: "Signature Deferred",

      BRS_REVIEW: "BRS Review",
      BRS_CORRECTIONS: "BRS Corrections",
      BRS_REVIEW_II: "BRS Review II",
      BRS_APPROVAL: "BRS Approval",
      PENDING_INCORPORATION: "Pending Incorporation",
      PENDING_INCORPORATION_APPROVAL: "Pending Incorporation Approval",

      INCORPORATION_DEFERRED: "Incorporation Deferred",

      VETTING: "Vetting",
      DEFERRED: "Deferred",
      REJECTED: "Rejected",
      ACTIVE: "Active",

      STRIKE_OFF_ONGOING: "Strike Off Ongoing",
      STRUCK_OFF: "Struck Off",
    };
  }

  static get SUBSCRIPTION_NAMES() {
    return {
      BASIC: "Basic",
      PRINT: "Print",
      PREMIUM: "Premium",
    };
  }
}

const EXCLUDED_APPLICANT_CAPACITIES = [
  Constants.APPLICANT_CAPACITIES.BENEFICIAL_OWNER,
  Constants.APPLICANT_CAPACITIES.ADVOCATE,
  Constants.APPLICANT_CAPACITIES.SHAREHOLDER,
  Constants.APPLICANT_CAPACITIES.JOINT_SHAREHOLDER,
];

const EXCLUDED_APPLICANT_CAPACITIES_FOR_CHANGE_OF_PARTICULARS = [
  Constants.APPLICANT_CAPACITIES.BENEFICIAL_OWNER,
  Constants.APPLICANT_CAPACITIES.ADVOCATE,
];

export const CAPITAL_INCREASE_TRANSACTION_TYPES = [
  "AUTHORIZATION",
  "ALLOTMENT",
  "AUTHORIZATION-AND-ALLOTMENT",
];
